"use client";

import { Component, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isServerActionError: boolean;
}

/**
 * Error Boundary que captura erros de Server Action desatualizada
 * e mostra uma UI amigável pedindo reload.
 */
export class ServerActionErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      isServerActionError: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Detecta se é um erro de Server Action (específico)
    const isServerActionError =
      error.message?.includes("Failed to find Server Action") ||
      error.message?.includes("older or newer deployment");

    return {
      hasError: true,
      isServerActionError,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ServerActionErrorBoundary caught error:", error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // UI de erro para Server Action desatualizada
      if (this.state.isServerActionError) {
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
            <div className="mx-4 max-w-md rounded-lg border bg-card p-6 shadow-lg">
              <div className="flex flex-col items-center text-center">
                <div className="mb-4 rounded-full bg-amber-100 p-3 dark:bg-amber-900/30">
                  <RefreshCw className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                </div>
                
                <h2 className="mb-2 text-xl font-semibold text-foreground">
                  Sessão Expirada
                </h2>
                
                <p className="mb-4 text-muted-foreground">
                  O sistema foi atualizado. Recarregue a página para continuar.
                </p>
                
                <button
                  onClick={this.handleReload}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Recarregar
                </button>
              </div>
            </div>
          </div>
        );
      }

      // UI de erro genérico
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
          <div className="mx-4 max-w-md rounded-lg border bg-card p-6 shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 rounded-full bg-red-100 p-3 dark:bg-red-900/30">
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              
              <h2 className="mb-2 text-xl font-semibold text-foreground">
                Ocorreu um Erro
              </h2>
              
              <p className="mb-4 text-muted-foreground">
                Algo deu errado. Por favor, tente recarregar a página.
              </p>
              
              <button
                onClick={this.handleReload}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                Recarregar Página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ServerActionErrorBoundary;
